import React, { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Card, Col, Form, Modal, Pagination, Row, Spinner, Tab, Table, Tabs } from 'react-bootstrap';
import { faPenToSquare, faTrash } from '@fortawesome/free-solid-svg-icons';
import ActionIconButton from '../../components/ActionIconButton';
import { deleteInventoryRow, getInventoryRows, getUsers, saveInventoryRow } from '../../service/apiService';
import './UserManagement.css';

const entityConfigs = {
  assets: {
    title: 'Asset Registry',
    fields: ['asset_code', 'name', 'category_name', 'serial_no', 'model', 'vendor_name', 'location_name', 'purchase_date', 'purchase_cost', 'warranty_expiry', 'assigned_to_name', 'status'],
    required: ['asset_code', 'name'],
  },
  categories: { title: 'Categories & Stock', fields: ['name', 'type', 'description', 'reorder_level', 'current_stock', 'status'], required: ['name'] },
  vendors: { title: 'Vendors', fields: ['name', 'contact_person', 'email', 'phone', 'gst_no', 'address', 'status'], required: ['name'] },
  locations: { title: 'Locations', fields: ['name', 'floor', 'room_no', 'custodian', 'status'], required: ['name'] },
  assignments: { title: 'Asset Assignments', fields: ['asset_id', 'asset_code', 'assigned_to_user_id', 'assigned_to_name', 'department', 'assigned_date', 'return_date', 'status', 'notes'], required: ['asset_id', 'assigned_to_name', 'assigned_date'] },
  maintenance: { title: 'Maintenance', fields: ['asset_id', 'asset_code', 'issue_title', 'issue_description', 'vendor_name', 'reported_date', 'resolved_date', 'cost', 'status'], required: ['asset_id', 'issue_title'] },
  stock: { title: 'Stock Movements', fields: ['category_name', 'movement_type', 'quantity', 'reference_no', 'movement_date', 'remarks'], required: ['category_name', 'movement_type', 'quantity'] },
  requests: { title: 'Requests & Approvals', fields: ['request_no', 'requested_by_user_id', 'requested_by_name', 'item_name', 'quantity', 'priority', 'reason', 'status', 'approved_by_name'], required: ['requested_by_name', 'item_name', 'quantity'] },
};

const emptyFor = (entity) => entityConfigs[entity].fields.reduce((acc, field) => ({ ...acc, [field]: '' }), { id: 0 });
const statusVariant = (status) => ({ available: 'success', active: 'success', approved: 'success', assigned: 'primary', pending: 'warning', maintenance: 'warning', retired: 'secondary', rejected: 'danger', completed: 'success' }[status] || 'secondary');

function labelize(field) {
  return field.replaceAll('_', ' ');
}

function inputType(field) {
  if (field.includes('date') || field.includes('expiry')) return 'date';
  if (['purchase_cost', 'cost', 'quantity', 'reorder_level', 'current_stock', 'asset_id', 'category_id', 'assigned_to_user_id', 'requested_by_user_id'].includes(field)) return 'number';
  if (field === 'email') return 'email';
  return 'text';
}

function InventoryManagement() {
  const [activeEntity, setActiveEntity] = useState('assets');
  const [rows, setRows] = useState([]);
  const [reports, setReports] = useState(null);
  const [lookups, setLookups] = useState({ assets: [], categories: [], vendors: [], locations: [], users: [] });
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total_pages: 1 });
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(emptyFor('assets'));
  const [showModal, setShowModal] = useState(false);

  const config = useMemo(() => entityConfigs[activeEntity], [activeEntity]);

  const loadLookups = async () => {
    const [assetRes, categoryRes, vendorRes, locationRes, userRes] = await Promise.all([
      getInventoryRows('assets', { limit: 100 }),
      getInventoryRows('categories', { limit: 100 }),
      getInventoryRows('vendors', { limit: 100 }),
      getInventoryRows('locations', { limit: 100 }),
      getUsers(),
    ]);
    setLookups({
      assets: assetRes.status === 'success' ? assetRes.data.rows || [] : [],
      categories: categoryRes.status === 'success' ? categoryRes.data.rows || [] : [],
      vendors: vendorRes.status === 'success' ? vendorRes.data.rows || [] : [],
      locations: locationRes.status === 'success' ? locationRes.data.rows || [] : [],
      users: Array.isArray(userRes) ? userRes : [],
    });
  };

  const loadRows = async (page = 1) => {
    setLoading(true);
    try {
      const [listRes, reportRes] = await Promise.all([
        getInventoryRows(activeEntity, { search, status, page, limit: pagination.limit }),
        getInventoryRows('reports'),
      ]);
      if (listRes.status === 'success') {
        setRows(listRes.data.rows || []);
        setPagination(listRes.data.pagination || pagination);
      }
      if (reportRes.status === 'success') setReports(reportRes.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setForm(emptyFor(activeEntity));
    loadRows(1);
    loadLookups();
  }, [activeEntity, search, status]);

  const openCreate = () => {
    setForm(emptyFor(activeEntity));
    loadLookups();
    setShowModal(true);
  };

  const openEdit = (row) => {
    setForm({ ...emptyFor(activeEntity), ...row });
    loadLookups();
    setShowModal(true);
  };

  const handleSave = async (event) => {
    event.preventDefault();
    const res = await saveInventoryRow(activeEntity, form);
    if (res.status === 'success') {
      setShowModal(false);
      await loadRows(pagination.page);
      await loadLookups();
    } else {
      alert(res.message || 'Unable to save inventory record');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this inventory record?')) return;
    const res = await deleteInventoryRow(activeEntity, id);
    if (res.status === 'success') {
      await loadRows(pagination.page);
      await loadLookups();
    } else {
      alert(res.message || 'Unable to delete record');
    }
  };

  const setAssetFromId = (assetId) => {
    const asset = lookups.assets.find((item) => String(item.id) === String(assetId));
    setForm({ ...form, asset_id: assetId, asset_code: asset?.asset_code || '' });
  };

  const setCategoryFromName = (name) => {
    const category = lookups.categories.find((item) => item.name === name);
    setForm({ ...form, category_name: name, category_id: category?.id || '' });
  };

  const setVendorFromName = (name) => {
    const vendor = lookups.vendors.find((item) => item.name === name);
    setForm({ ...form, vendor_name: name, vendor_id: vendor?.id || '' });
  };

  const setLocationFromName = (name) => {
    const location = lookups.locations.find((item) => item.name === name);
    setForm({ ...form, location_name: name, location_id: location?.id || '' });
  };

  const setUserFromId = (fieldId, fieldName, userId) => {
    const user = lookups.users.find((item) => String(item.id) === String(userId));
    setForm({ ...form, [fieldId]: userId, [fieldName]: user?.name || '' });
  };

  const renderFieldControl = (field) => {
    if (field === 'category_name') {
      return (
        <Form.Select value={form.category_name || ''} onChange={(e) => setCategoryFromName(e.target.value)} required={config.required.includes(field)}>
          <option value="">Select category</option>
          {lookups.categories.map((item) => <option key={item.id} value={item.name}>{item.name}</option>)}
        </Form.Select>
      );
    }
    if (field === 'vendor_name') {
      return (
        <Form.Select value={form.vendor_name || ''} onChange={(e) => setVendorFromName(e.target.value)}>
          <option value="">Select vendor</option>
          {lookups.vendors.map((item) => <option key={item.id} value={item.name}>{item.name}</option>)}
        </Form.Select>
      );
    }
    if (field === 'location_name') {
      return (
        <Form.Select value={form.location_name || ''} onChange={(e) => setLocationFromName(e.target.value)}>
          <option value="">Select location</option>
          {lookups.locations.map((item) => <option key={item.id} value={item.name}>{item.name}</option>)}
        </Form.Select>
      );
    }
    if (field === 'asset_id') {
      return (
        <Form.Select value={form.asset_id || ''} onChange={(e) => setAssetFromId(e.target.value)} required={config.required.includes(field)}>
          <option value="">Select asset</option>
          {lookups.assets.map((item) => <option key={item.id} value={item.id}>{item.asset_code} - {item.name}</option>)}
        </Form.Select>
      );
    }
    if (field === 'assigned_to_user_id') {
      return (
        <Form.Select value={form.assigned_to_user_id || ''} onChange={(e) => setUserFromId('assigned_to_user_id', 'assigned_to_name', e.target.value)}>
          <option value="">Select employee</option>
          {lookups.users.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
        </Form.Select>
      );
    }
    if (field === 'requested_by_user_id') {
      return (
        <Form.Select value={form.requested_by_user_id || ''} onChange={(e) => setUserFromId('requested_by_user_id', 'requested_by_name', e.target.value)}>
          <option value="">Select requester</option>
          {lookups.users.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
        </Form.Select>
      );
    }
    if (field === 'status') {
      const options = activeEntity === 'assets'
        ? ['available', 'assigned', 'maintenance', 'retired']
        : activeEntity === 'requests'
          ? ['pending', 'approved', 'rejected', 'issued', 'closed']
          : activeEntity === 'maintenance'
            ? ['open', 'in_progress', 'completed']
            : activeEntity === 'assignments'
              ? ['assigned', 'returned']
              : ['active', 'inactive'];
      return <Form.Select value={form.status || ''} onChange={(e) => setForm({ ...form, status: e.target.value })}>{options.map((item) => <option key={item} value={item}>{item}</option>)}</Form.Select>;
    }
    if (field === 'movement_type') {
      return <Form.Select value={form.movement_type || ''} onChange={(e) => setForm({ ...form, movement_type: e.target.value })}><option value="">Select type</option><option value="in">In</option><option value="out">Out</option><option value="adjustment">Adjustment</option></Form.Select>;
    }
    if (field === 'priority') {
      return <Form.Select value={form.priority || ''} onChange={(e) => setForm({ ...form, priority: e.target.value })}><option value="">Select priority</option><option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="urgent">Urgent</option></Form.Select>;
    }
    return (
      <Form.Control
        as={['address', 'description', 'issue_description', 'reason', 'remarks', 'notes'].includes(field) ? 'textarea' : 'input'}
        rows={3}
        type={inputType(field)}
        value={form[field] || ''}
        onChange={(e) => setForm({ ...form, [field]: e.target.value })}
        required={config.required.includes(field)}
      />
    );
  };

  return (
    <div className="hr-module-page">
      <div className="page-title-row">
        <div>
          <span>Corporate Assets</span>
          <h3>Inventory Management</h3>
        </div>
        <Button onClick={openCreate}>Add {config.title}</Button>
      </div>

      <Row className="g-3">
        {[
          ['Total Assets', reports?.total_assets || 0],
          ['Available', reports?.available_assets || 0],
          ['Assigned', reports?.assigned_assets || 0],
          ['Asset Value', `Rs. ${Number(reports?.asset_value || 0).toFixed(0)}`],
        ].map(([title, value]) => (
          <Col md={3} sm={6} key={title}>
            <Card className="erp-panel-card"><Card.Body><span className="text-muted small">{title}</span><h4 className="mb-0">{value}</h4></Card.Body></Card>
          </Col>
        ))}
      </Row>

      <Tabs activeKey={activeEntity} onSelect={(key) => setActiveEntity(key || 'assets')} className="erp-tabs">
        {Object.entries(entityConfigs).map(([key, item]) => <Tab key={key} eventKey={key} title={item.title} />)}
      </Tabs>

      <Card className="erp-panel-card">
        <Card.Body>
          <Row className="g-2 mb-3">
            <Col md={5}><Form.Control placeholder={`Search ${config.title.toLowerCase()}`} value={search} onChange={(e) => setSearch(e.target.value)} /></Col>
            <Col md={3}><Form.Control placeholder="Filter status" value={status} onChange={(e) => setStatus(e.target.value)} /></Col>
          </Row>

          {loading ? <div className="table-loader"><Spinner animation="border" /></div> : (
            <Table responsive hover className="erp-table">
              <thead>
                <tr>
                  {config.fields.slice(0, 6).map((field) => <th key={field}>{labelize(field)}</th>)}
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    {config.fields.slice(0, 6).map((field) => (
                      <td key={field}>{field === 'status' ? <Badge bg={statusVariant(row[field])}>{row[field] || '-'}</Badge> : row[field] || '-'}</td>
                    ))}
                    <td>
                      <div className="erp-action-row">
                        <ActionIconButton size="sm" variant="outline-primary" icon={faPenToSquare} label="Edit" onClick={() => openEdit(row)} />
                        <ActionIconButton size="sm" variant="outline-danger" icon={faTrash} label="Delete" onClick={() => handleDelete(row.id)} />
                      </div>
                    </td>
                  </tr>
                ))}
                {!rows.length && <tr><td colSpan={config.fields.slice(0, 6).length + 1} className="text-center text-muted">No records found</td></tr>}
              </tbody>
            </Table>
          )}

          {pagination.total_pages > 1 && (
            <Pagination className="justify-content-end">
              <Pagination.Prev disabled={pagination.page <= 1} onClick={() => loadRows(pagination.page - 1)} />
              {[...Array(pagination.total_pages)].map((_, index) => (
                <Pagination.Item key={index + 1} active={pagination.page === index + 1} onClick={() => loadRows(index + 1)}>{index + 1}</Pagination.Item>
              ))}
              <Pagination.Next disabled={pagination.page >= pagination.total_pages} onClick={() => loadRows(pagination.page + 1)} />
            </Pagination>
          )}
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
        <Form onSubmit={handleSave}>
          <Modal.Header closeButton><Modal.Title>{form.id ? 'Edit' : 'Add'} {config.title}</Modal.Title></Modal.Header>
          <Modal.Body>
            <Row className="g-3">
              {config.fields.map((field) => (
                <Col md={['address', 'description', 'issue_description', 'reason', 'remarks', 'notes'].includes(field) ? 12 : 6} key={field}>
                  <Form.Label className="text-capitalize">{labelize(field)}</Form.Label>
                  {renderFieldControl(field)}
                </Col>
              ))}
            </Row>
          </Modal.Body>
          <Modal.Footer><Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button><Button type="submit">Save</Button></Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}

export default InventoryManagement;
